
import React from 'react';
import { i18nManager } from './i18n';
import ReactDOM from 'react-dom/client';
import Compound from './components/Compound';
import MediaInput from './components/shared/MediaInput';
import { Elements } from './components/shared/Elements';
import { CacheManager, LzEncryptor, Utils } from './utils';
import { ConfirmDialog } from './components/shared/ConfirmDialog';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { Ploutous, Environment as PloutousEnvironment } from '@ploutous/react-library';
import { ButtonManageRef, Debugger, NoseurObject, Position, Toaster } from '@rivtn/noseur';
import { BaseService, ConsusService, CountryService, ICountry, IMember, IProfile, MemberService, PlatformService, SanitizedResponse } from './services';
import { LocalStorageFeatureFlagSource, OyaGbewa, SessionStorageFeatureFlagSource, UrlSearchParamsFeatureFlagSource } from '@syllektis/oya-gbewa-react';
import './assets/css/index.css';
import './assets/css/scheme.css';
import './assets/css/mobile.css';

OyaGbewa.initFeatureFlags({
    environment: process.env.REACT_APP_ENV,
    sources: [
        SessionStorageFeatureFlagSource.getInstance({ ignoreEvents: true }),
        LocalStorageFeatureFlagSource.getInstance({ injectSameWindowEventTrigger: true }),
        UrlSearchParamsFeatureFlagSource.getInstance({ key: "featureFlags", emptyStringIsBoolean: true }),
    ],
    flags: {
        darkModeToggle: false,
        languageToggle: false,
        enableTournamentFeed: false,
    },
});
// TODO load from api
// OyaGbewa.initPlan({
//     BASIC: {
//         usernameChange: 0,
//         changeAiImageGenerationPrompt: false,
//         profileBannerGenerationWithAi: false,
//         profilePictureGenerationWithAi: false,
//         enableProfileWebsiteGalleryPage: false,
//         toggleOmnehostDataUseForAiTraining: false,
//     },
//     PROFESSIONAL: {
//         usernameChange: 2,
//         profileBannerGenerationWithAi: true,
//         changeAiImageGenerationPrompt: false,
//         profilePictureGenerationWithAi: true,
//         enableProfileWebsiteGalleryPage: true,
//         toggleOmnehostDataUseForAiTraining: true,
//     },
//     BUSINESS: {
//         usernameChange: 5,
//         changeAiImageGenerationPrompt: true,
//         profileBannerGenerationWithAi: true,
//         profilePictureGenerationWithAi: true,
//         enableProfileWebsiteGalleryPage: true,
//         toggleOmnehostDataUseForAiTraining: true,
//     },
// });

Debugger.setNamespace("OMNEHOST.WEB_CLIENT");
const searchParams = new URLSearchParams(window.location.search);

if (searchParams.has("pas")) {
    const pas = JSON.parse(LzEncryptor.decrypt(searchParams.get("pas"), true)) as any;
    Debugger.log("Index:useEffect:pas", searchParams.get("pas"), "\n=>\n", pas);
    CacheManager.insecurePut(CacheManager.ACCESS_TOKEN_KEY, pas.a);
    searchParams.delete("pas");
    CacheManager.put(CacheManager.SESSION_TOKEN_KEY, pas.s);
    CacheManager.put(CacheManager.PROFILE_KEY, pas.p);

    window.history.pushState({}, document.title, window.location.pathname + (!!searchParams.size ? `?${searchParams.toString()}` : ""));
}
if (!CacheManager.has(CacheManager.PROFILE_KEY)) {
    Debugger.log("Trigger signin redirect...");
    Utils.signIn();
} else {
    Ploutous.init({
        serviceCode: "omnehost",
        businessCode: "omnehost",
        environment: PloutousEnvironment.staging,
        language: i18nManager.ActiveLanguage as any ?? "en",
        __base_url_do_not_set__: "https://api.uat.ploutous.com",
    });

    const labels = i18nManager.Labels;
    const root = document.getElementById("root");
    const dialogErrorRef = { current: null } as any;
    const consusService = ConsusService.getInstance();
    const memberService = MemberService.getInstance();
    const countryService = CountryService.getInstance();
    const platformService = PlatformService.getInstance();
    const janusLaunariusProfile = CacheManager.get(CacheManager.PROFILE_KEY) as IProfile;
    memberService.getProfile(true).then(async ({ sanitized }: SanitizedResponse<IMember>) => {
        const plans = (await platformService.queryPlans()).content?.map((c) => ({ features: c.features_mapping, code: c.code }));
        OyaGbewa.initPlan(plans.reduce((acc, plan) => {
            acc[plan.code] = Object.entries(plan.features).reduce((a, [k, v]) => {
                a[Utils.toCamelCase(k)] = v;
                return a;
            }, {} as any);
            return acc;
        }, {} as any));
        OyaGbewa.setActivePlanName(sanitized.plan);
        Debugger.log("OyaGbewa.Plan", OyaGbewa.getPlan());
        Utils.PROFILE = sanitized;
        render(sanitized);
    }).catch(async (err: any) => {
        if (err?.response?.status === 404) {
            let countries: ICountry[] = [];
            try {
                const { sanitized: { content } } = await countryService.queryCountries();
                countries = content;
            } catch (err: any) {
                Debugger.error("DASHBOARD.PROFILE.CREATE", err);
            }
            const payload: NoseurObject<any> = {
                profile_picture_location: janusLaunariusProfile?.profile_picture_location
            };
            const createProfileLabels = labels.compound.popover.create_profile;
            createProfileLabels.form.forEach((i) => {
                if (i.name === "email") i.value = janusLaunariusProfile.email;
                if (i.name === "country_code") i.options = countries.map((country) => ({ value: country.code, label: country.name }));
            });
            const dialog = ConfirmDialog({
                style: { minWidth: 400, maxWidth: "80%" },
                confirmLabel: createProfileLabels.create_profile,
                title: createProfileLabels.create_your_profile,
                content: (<div className="dialog-content">
                    <MediaInput style={{ alignSelf: "center" }} default={janusLaunariusProfile?.profile_picture_location}
                        onSelectFiles={(files) => payload.profile_picture_location = files[0]} onGenerateWithAi={async () => {
                            return new File(["We are here"], "we.txt", { type: 'text/plain' });
                        }} />
                    {Elements.buildConfirmDialogForm(createProfileLabels.form, (name: string, value: any, searching: any) => {
                        dialogErrorRef.current!.innerText = " ";
                        if (searching && name === "country_code") {
                            countryService.queryCountries({ name: value.value }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
                                value.manageRef?.changeOptions(apiResponse.data!.content?.map((country) => ({ value: country.code, label: country.name })));
                            }).catch(BaseService.reportError);
                        }
                        payload[name] = value;
                        return false;
                    }, payload, undefined, { marginTop: 0 })}
                    <div ref={dialogErrorRef} className="error"> </div>
                </div>),
                onConfirm: async (button?: ButtonManageRef) => {
                    dialogErrorRef.current!.innerText = " ";
                    if (Object.keys(payload).length < 2) {
                        dialogErrorRef.current!.innerText = createProfileLabels.missing_required_fields;
                        return false;
                    }
                    if (Utils.hasSpaceOrSpecial(payload.username)) {
                        dialogErrorRef.current!.innerText = createProfileLabels.has_space_or_special;
                        return false;
                    }
                    button?.setLoadingState(true);
                    const pictureFile = payload.profile_picture_location;
                    const picIsFile = pictureFile && typeof pictureFile !== "string";
                    if (picIsFile) {
                        payload.profile_picture_url = "UPLOADING";
                    }
                    payload.verify_email_prefix = `${window.location.origin}/verify`;
                    memberService.upsertProfile(payload).then(async ({ sanitized }: SanitizedResponse<IMember>) => {
                        dialog.hide();
                        if (picIsFile) {
                            try {
                                const { sanitized: picSanitized } = await consusService.uploadFile("/omnehost/member/profile/", `pro_pic_${sanitized?.janus_user_external_id}`, pictureFile);
                                sanitized.profile_picture_url = picSanitized.consus_download_url;
                                memberService.upsertProfile(sanitized).then(() => { }).catch(BaseService.reportError);
                            } catch (err: any) {
                                BaseService.reportError(err);
                            }
                        }
                        render(sanitized);
                    }).catch(({ errorMessage }: SanitizedResponse<any>) => {
                        dialogErrorRef.current!.innerText = errorMessage;
                    }).finally(() => {
                        button?.setLoadingState(false);
                    });
                    return false;
                },
            });
            dialog.show();
            return;
        }
        renderError(root);
        BaseService.reportError(err);
    });
}

function render(profile: IMember) {
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
        //<React.StrictMode>
        <RouterProvider router={createBrowserRouter([
            { path: "/*", element: <Compound profile={profile} /> },
        ])} />
        //</React.StrictMode>
    );
    Toaster.init({ position: Position.TOP_CENTER });
}

function renderError(root: HTMLElement | null) {
    if (!root) return;
    root.innerHTML = `<div style="display: flex; flex-direction: column; align-items: center;">
        <span style="color: white;">Service Unreachable</span>
        <span style="color: #479FC8; font-weight: bold; margin-top: 5px; cursor: pointer;" onclick="window.location.reload()">Refresh</span>
    <div>`;
}

if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js');
    });
}